/* ==========================================================================
   RIZEN — Story Reels
   Powers sections/rizen-story-reels.liquid
   - muted autoplay previews for in-view cards (IntersectionObserver)
   - full-screen story viewer: segmented progress, autoplay, mute toggle,
     tap / swipe / keyboard navigation, "Shop this watch" CTA
   No dependencies. Safe to load once even if multiple sections exist.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__rizenReelsInit) return;
  window.__rizenReelsInit = true;

  var SVG = {
    close: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>',
    muted: '<svg class="icon-muted" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H3v6h3l5 4V5z"/><path d="M22 9l-6 6M16 9l6 6"/></svg>',
    unmuted: '<svg class="icon-unmuted" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:none"><path d="M11 5 6 9H3v6h3l5 4V5z"/><path d="M15.5 8.5a5 5 0 0 1 0 7M19 5a9 9 0 0 1 0 14"/></svg>',
    prev: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>',
    next: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>',
    cart: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.7 13.4a2 2 0 0 0 2 1.6h9.7a2 2 0 0 0 2-1.6L23 6H6"/></svg>'
  };

  var IMG_FALLBACK_TIMEOUT = 8000; // hard cap so a broken clip auto-advances

  /* ---------------------------------------------------------------------- *
   *  Shared full-screen viewer (built lazily, reused by every section)
   * ---------------------------------------------------------------------- */
  var Viewer = (function () {
    var root, stage, video, progress, avatar, nameEl, roleEl, cta, navPrev, navNext;
    var reels = [];
    var index = 0;
    var muted = true;
    var built = false;
    var failTimer = null;

    function build() {
      if (built) return;
      built = true;

      root = document.createElement('div');
      root.className = 'rizen-reels-viewer';
      root.setAttribute('role', 'dialog');
      root.setAttribute('aria-modal', 'true');
      root.setAttribute('aria-label', 'Story viewer');
      root.innerHTML =
        '<button class="rizen-reels-viewer__nav rizen-reels-viewer__nav--prev" aria-label="Previous story">' + SVG.prev + '</button>' +
        '<button class="rizen-reels-viewer__close-abs" aria-label="Close">' + SVG.close + '</button>' +
        '<div class="rizen-reels-viewer__stage">' +
          '<video class="rizen-reels-viewer__video" playsinline webkit-playsinline preload="auto"></video>' +
          '<div class="rizen-reels-viewer__progress"></div>' +
          '<div class="rizen-reels-viewer__head">' +
            '<div class="rizen-reels-viewer__who">' +
              '<span class="rizen-reels-viewer__avatar"></span>' +
              '<span class="rizen-reels-viewer__names">' +
                '<span class="rizen-reels-viewer__name"></span>' +
                '<span class="rizen-reels-viewer__role"></span>' +
              '</span>' +
            '</div>' +
            '<div class="rizen-reels-viewer__tools">' +
              '<button class="rizen-reels-viewer__btn rizen-reels-viewer__btn--mute" aria-label="Toggle sound">' + SVG.muted + SVG.unmuted + '</button>' +
              '<button class="rizen-reels-viewer__btn rizen-reels-viewer__btn--close" aria-label="Close">' + SVG.close + '</button>' +
            '</div>' +
          '</div>' +
          '<button class="rizen-reels-viewer__zone rizen-reels-viewer__zone--prev" aria-label="Previous"></button>' +
          '<button class="rizen-reels-viewer__zone rizen-reels-viewer__zone--next" aria-label="Next"></button>' +
          '<a class="rizen-reels-viewer__cta" href="#">' + SVG.cart + '<span class="rizen-reels-viewer__cta-text"></span></a>' +
        '</div>' +
        '<button class="rizen-reels-viewer__nav rizen-reels-viewer__nav--next" aria-label="Next story">' + SVG.next + '</button>';

      document.body.appendChild(root);

      stage    = root.querySelector('.rizen-reels-viewer__stage');
      video    = root.querySelector('.rizen-reels-viewer__video');
      progress = root.querySelector('.rizen-reels-viewer__progress');
      avatar   = root.querySelector('.rizen-reels-viewer__avatar');
      nameEl   = root.querySelector('.rizen-reels-viewer__name');
      roleEl   = root.querySelector('.rizen-reels-viewer__role');
      cta      = root.querySelector('.rizen-reels-viewer__cta');
      navPrev  = root.querySelector('.rizen-reels-viewer__nav--prev');
      navNext  = root.querySelector('.rizen-reels-viewer__nav--next');

      // controls
      root.querySelector('.rizen-reels-viewer__zone--prev').addEventListener('click', prev);
      root.querySelector('.rizen-reels-viewer__zone--next').addEventListener('click', next);
      navPrev.addEventListener('click', prev);
      navNext.addEventListener('click', next);
      root.querySelector('.rizen-reels-viewer__btn--mute').addEventListener('click', toggleMute);
      root.querySelector('.rizen-reels-viewer__btn--close').addEventListener('click', close);
      root.querySelector('.rizen-reels-viewer__close-abs').addEventListener('click', close);

      // click the dim backdrop (outside the stage) to close
      root.addEventListener('click', function (e) {
        if (e.target === root) close();
      });

      // video events
      video.addEventListener('timeupdate', onTime);
      video.addEventListener('ended', next);
      video.addEventListener('error', onFail);
      video.addEventListener('playing', clearFail);

      // keyboard
      document.addEventListener('keydown', onKey);

      // swipe
      addSwipe(stage);
    }

    function fillProgress(count) {
      progress.innerHTML = '';
      for (var i = 0; i < count; i++) {
        var seg = document.createElement('div');
        seg.className = 'rizen-reels-viewer__seg';
        seg.innerHTML = '<i></i>';
        progress.appendChild(seg);
      }
    }

    function setSegments() {
      var segs = progress.querySelectorAll('.rizen-reels-viewer__seg');
      for (var i = 0; i < segs.length; i++) {
        var bar = segs[i].querySelector('i');
        if (i < index) { segs[i].classList.add('is-done'); bar.style.width = '100%'; }
        else { segs[i].classList.remove('is-done'); bar.style.width = '0'; }
      }
    }

    function onTime() {
      var segs = progress.querySelectorAll('.rizen-reels-viewer__seg');
      var active = segs[index];
      if (!active || !video.duration || isNaN(video.duration)) return;
      active.querySelector('i').style.width = (video.currentTime / video.duration * 100) + '%';
    }

    function onFail() { scheduleFail(); }
    function scheduleFail() {
      clearFail();
      failTimer = setTimeout(next, 1200);
    }
    function clearFail() {
      if (failTimer) { clearTimeout(failTimer); failTimer = null; }
    }

    function load(i) {
      index = Math.max(0, Math.min(i, reels.length - 1));
      var r = reels[index];
      if (!r) return;

      setSegments();

      // meta
      nameEl.textContent = r.name || '';
      roleEl.textContent = r.role || '';
      if (r.avatar) {
        avatar.innerHTML = '<img src="' + r.avatar + '" alt="' + (r.name || '') + '">';
      } else {
        avatar.innerHTML = '';
      }

      // CTA
      if (r.ctaUrl) {
        cta.href = r.ctaUrl;
        cta.querySelector('.rizen-reels-viewer__cta-text').textContent = r.ctaText || 'Shop now';
        cta.hidden = false;
      } else {
        cta.hidden = true;
      }

      // arrows availability
      navPrev.disabled = index <= 0;
      navNext.disabled = false; // next on last closes

      // video
      clearFail();
      video.muted = muted;
      if (r.poster) video.poster = r.poster;
      video.src = r.video;
      video.currentTime = 0;
      var p = video.play();
      if (p && p.catch) {
        p.catch(function () {
          // autoplay with sound was blocked -> retry muted
          video.muted = true;
          muted = true;
          root.classList.remove('is-unmuted');
          video.play().catch(scheduleFail);
        });
      }
    }

    function open(list, startIndex, startMuted) {
      build();
      reels = list;
      muted = startMuted !== false;
      root.classList.toggle('is-unmuted', !muted);
      fillProgress(reels.length);
      document.documentElement.classList.add('rizen-reels-lock');
      document.body.classList.add('rizen-reels-lock');
      root.classList.add('is-open');
      load(startIndex || 0);
    }

    function close() {
      if (!built) return;
      clearFail();
      root.classList.remove('is-open');
      document.documentElement.classList.remove('rizen-reels-lock');
      document.body.classList.remove('rizen-reels-lock');
      try { video.pause(); } catch (e) {}
      video.removeAttribute('src');
      video.load();
    }

    function next() {
      if (index >= reels.length - 1) { close(); return; }
      load(index + 1);
    }
    function prev() {
      if (index <= 0) { load(0); return; }
      load(index - 1);
    }

    function toggleMute() {
      muted = !muted;
      video.muted = muted;
      root.classList.toggle('is-unmuted', !muted);
      if (!muted && video.paused) video.play().catch(function () {});
    }

    function isOpen() { return built && root.classList.contains('is-open'); }

    function onKey(e) {
      if (!isOpen()) return;
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'm' || e.key === 'M') toggleMute();
    }

    function addSwipe(el) {
      var x0 = null, y0 = null;
      el.addEventListener('touchstart', function (e) {
        x0 = e.touches[0].clientX; y0 = e.touches[0].clientY;
      }, { passive: true });
      el.addEventListener('touchend', function (e) {
        if (x0 === null) return;
        var dx = e.changedTouches[0].clientX - x0;
        var dy = e.changedTouches[0].clientY - y0;
        if (Math.abs(dy) > Math.abs(dx) && dy > 70) { close(); }
        else if (Math.abs(dx) > 45) { dx < 0 ? next() : prev(); }
        x0 = y0 = null;
      }, { passive: true });
    }

    return { open: open };
  })();

  /* ---------------------------------------------------------------------- *
   *  Per-section wiring: previews + open-on-tap
   * ---------------------------------------------------------------------- */
  function reelData(card) {
    return {
      video: card.getAttribute('data-video'),
      poster: card.getAttribute('data-poster'),
      name: card.getAttribute('data-name'),
      role: card.getAttribute('data-role'),
      avatar: card.getAttribute('data-avatar'),
      ctaText: card.getAttribute('data-cta-text'),
      ctaUrl: card.getAttribute('data-cta-url')
    };
  }

  function initSection(section) {
    if (section.__rizenReels) return;
    section.__rizenReels = true;

    if (!section.querySelector('.rizen-reel')) return;

    var mode         = section.getAttribute('data-display-mode') === 'marquee' ? 'marquee' : 'carousel';
    var openInViewer = section.getAttribute('data-open-in-viewer') !== 'false';
    var previews     = section.getAttribute('data-autoplay-previews') !== 'false';
    var muteDefault  = section.getAttribute('data-mute-default') !== 'false';

    // Viewer list = original (non-clone) reels, in DOM order.
    function originals() {
      return Array.prototype.slice.call(section.querySelectorAll('.rizen-reel:not(.rizen-reel--clone)'));
    }
    function openAt(i) {
      var list = originals().map(reelData);
      if (!list.length) return;
      i = Math.max(0, Math.min(i, list.length - 1));
      if (openInViewer) {
        Viewer.open(list, i, muteDefault);
      } else if (list[i].ctaUrl) {
        window.location.href = list[i].ctaUrl;
      }
    }

    // Delegated open — works for marquee clones (created later) too, and
    // ignores clicks suppressed by a Flickity drag.
    section.addEventListener('click', function (e) {
      if (e.defaultPrevented) return;
      var card = e.target.closest && e.target.closest('.rizen-reel');
      if (!card || !section.contains(card)) return;
      openAt(parseInt(card.getAttribute('data-reel-index'), 10) || 0);
    });
    section.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var card = e.target.closest && e.target.closest('.rizen-reel');
      if (!card || card.classList.contains('rizen-reel--clone')) return;
      e.preventDefault();
      openAt(parseInt(card.getAttribute('data-reel-index'), 10) || 0);
    });

    if (mode === 'marquee') {
      buildMarquee(section, previews);
      window.addEventListener('resize', debounce(function () { buildMarquee(section, previews); }, 250));
    } else {
      setupCarouselPreviews(section, previews);
    }
  }

  /* ---- preview observer (muted autoplay for in-view cards) ---------------- */
  function makePreviewObserver(root) {
    return new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var card = entry.target;
        var v = card.querySelector('.rizen-reel__video');
        if (!v) return;
        if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
          v.muted = true;
          var p = v.play();
          if (p && p.then) p.then(function () { card.classList.add('is-previewing'); }).catch(function () {});
        } else {
          v.pause();
          card.classList.remove('is-previewing');
        }
      });
    }, { root: root, threshold: [0, 0.5, 1] });
  }

  function setupCarouselPreviews(section, previews) {
    if (!(previews && 'IntersectionObserver' in window && !prefersReducedMotion())) return;
    var carousel = section.querySelector('.rizen-reels__carousel');
    var start = function () {
      var root = (carousel && carousel.querySelector('.flickity-viewport')) || carousel || null;
      var io = makePreviewObserver(root);
      section.querySelectorAll('.rizen-reel').forEach(function (c) { io.observe(c); });
    };
    if (carousel && !carousel.querySelector('.flickity-viewport')) setTimeout(start, 400);
    else start();
  }

  /* ---- marquee: size cards, clone for a seamless loop, set speed --------- */
  function sizeMarqueeCards(section, mq) {
    var cs = getComputedStyle(section);
    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    var cols = parseFloat(isMobile ? cs.getPropertyValue('--rr-cols-mobile') : cs.getPropertyValue('--rr-cols')) || (isMobile ? 1.2 : 4);
    var gap = parseFloat(cs.getPropertyValue('--rr-gap')) || 16;
    var w = mq.clientWidth;
    if (!w) return;
    var card = (w - (cols - 1) * gap) / cols;
    section.style.setProperty('--rr-marquee-card-width', Math.round(card) + 'px');
  }

  function buildMarquee(section, previews) {
    var mq = section.querySelector('.rizen-reels__marquee');
    if (!mq) return;
    var track = mq.querySelector('.rizen-reels__marquee-track');
    if (!track) return;

    if (section.__rrIO) { section.__rrIO.disconnect(); section.__rrIO = null; }
    Array.prototype.slice.call(track.querySelectorAll('.rizen-reel--clone')).forEach(function (c) { c.remove(); });

    sizeMarqueeCards(section, mq);

    var originals = Array.prototype.slice.call(track.querySelectorAll('.rizen-reel'));
    if (!originals.length) return;

    var cs = getComputedStyle(section);
    var gap = parseFloat(cs.getPropertyValue('--rr-gap')) || 16;
    var count = originals.length;
    var sumW = originals.reduce(function (s, el) { return s + el.offsetWidth; }, 0);
    var pitch = sumW + gap * count;                 // width of one repeating set, incl. gaps
    var container = mq.clientWidth || pitch;
    var copies = Math.max(2, Math.ceil((container + pitch) / pitch));

    for (var c = 1; c < copies; c++) {
      originals.forEach(function (el) {
        var cl = el.cloneNode(true);
        cl.classList.add('rizen-reel--clone');
        cl.setAttribute('aria-hidden', 'true');
        cl.setAttribute('tabindex', '-1');
        cl.removeAttribute('data-reel');
        track.appendChild(cl);
      });
    }

    section.style.setProperty('--rr-marquee-shift', pitch + 'px');
    var speed = parseFloat(section.getAttribute('data-marquee-speed')) || 5;
    var pxPerSec = speed * 22;                        // speed 1–10 → 22–220 px/s
    section.style.setProperty('--rr-marquee-duration', (pitch / pxPerSec).toFixed(2) + 's');

    if (previews && 'IntersectionObserver' in window && !prefersReducedMotion()) {
      var io = makePreviewObserver(mq);
      track.querySelectorAll('.rizen-reel').forEach(function (cEl) { io.observe(cEl); });
      section.__rrIO = io;
    }
  }

  function debounce(fn, ms) {
    var t;
    return function () { var a = arguments, ctx = this; clearTimeout(t); t = setTimeout(function () { fn.apply(ctx, a); }, ms); };
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function initAll() {
    document.querySelectorAll('[data-rizen-reels]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor: re-init when a section is added/reloaded
  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target.querySelector ? e.target.querySelector('[data-rizen-reels]') : null;
    if (e.target.matches && e.target.matches('[data-rizen-reels]')) initSection(e.target);
    else if (s) initSection(s);
  });
})();
